import React, {useId, useMemo} from 'react';

import FieldDescription from '@pb/kit-component-field-description';
import {useListbox} from '@pb/kit-component-listbox';
import {useControlledState} from '@pb/kit-hooks-use-controlled-state';

import {ComboBoxInput} from './combo-box-input.component';
import {DefaultItem} from './combo-box-item-default.component';
import {ComboboxListbox} from './combo-box-listbox.component';
import {useComboboxValue} from './use-combobox-value';
import {iterateItems} from './utils/iterate-items';


import type {ComboBoxInputProps} from './combo-box-input.component';
import type {ComboboxItemProps} from './combo-box-item.component';
import type {ComboboxListboxProps} from './combo-box-listbox.component';
import type {UseListboxProps} from '@pb/kit-component-listbox';
import type {ReactElement, ReactNode} from 'react';


type _ComboBoxInputProps = Omit<ComboBoxInputProps, 'selectedItemsProps' | 'isDefaultItemSelected' | 'onChange' | 'onToggle'>;

export interface ComboBoxProps<Value> extends _ComboBoxInputProps {
    /**
     * Controls listbox visibility externally. When omitted the component manages
     * open/close state internally. Pair with `setShown` for fully controlled behavior.
     */
    shown?: boolean,

    /**
     * Setter for the controlled `shown` state. Called with `true` when the listbox
     * should open and `false` when it should close.
     */
    setShown?: (shown: boolean) => void,

    /**
     * Currently selected value. Pass a single `ItemValue` for single-select mode or
     * an `ItemValue[]` for multi-select — the component infers `multiselectable` from
     * whether `value` is an array. Omit for an uncontrolled selection.
     */
    value?: Value,

    /**
     * Descriptive text rendered below the input (e.g. validation message, hint).
     * Automatically linked to the input via `aria-describedby`.
     */
    helperText?: ReactNode,

    /**
     * HTML `name` attribute forwarded to the underlying `<input>`. Also passed as
     * the second argument to `onChange` so callers can identify the field in a
     * shared handler.
     */
    name?: string,
    /**
     * Called when the selected value changes. In multi-select mode receives the full
     * updated array; in single-select mode receives the newly selected scalar value.
     * The second argument is the `name` prop value.
     */
    onChange?: (value: Value, name: string) => void,

    /**
     * Extends the listbox width beyond the anchor when set.
     * `"left"` anchors to the right edge; `"right"` anchors to the left edge.
     * Omit to match the anchor width exactly.
     */
    direction?: 'left' | 'right',

    /**
     * Number of items visible before the listbox starts scrolling. Default `10`.
     */
    itemsForScroll?: number,

    /**
     * Called whenever the listbox opens or closes. Receives the new boolean state.
     * Useful for lazy-loading items on first open.
     */
    onToggle?: (shown: boolean) => void,
    /**
     * If true - Check icon on selected item will be hidden
     * @deprecated It will be removed in 6 months 09-15-2025. use Item children instead
     */
    dropdown?: boolean,
    /**
     * `<ComboboxItem>`, `<ComboboxGroup>`, and `<DefaultItem>` elements that populate
     * the listbox. Must be passed as direct children with no wrapper elements in between.
     * Other node types (e.g. a loading spinner) render inside the listbox but are not
     * treated as selectable items.
     */
    children?: ReactNode,

    /** Floating UI positioning strategy (`"absolute"` or `"fixed"`).
     *  Use `"fixed"` inside scrollable containers or modals. */
    type?: UseListboxProps['type'],

    /** Ordered list of Floating UI placement strings the listbox may use.
     *  The first placement that fits the viewport is selected automatically. */
    allowedPlacements?: UseListboxProps['allowedPlacements'],
    /** Props forwarded to the `<Portal>` that wraps the floating listbox.
     *  Use to control the portal mount target or add a class to the portal wrapper. */
    portalProps?: ComboboxListboxProps<unknown>['portalProps'],
}

/**
 * Select-one-or-many dropdown that displays the chosen value in a read-only
 * input field. Accepts `<ComboboxItem>`, `<ComboboxGroup>`, and `<DefaultItem>`
 * as direct children. Any other node (e.g. a loader) also renders in the listbox
 * but has no selection or keyboard navigation behavior.
 *
 * Single-select closes the listbox after a choice; multi-select (triggered by
 * passing an array as `value`) keeps it open and toggles checkmarks. A
 * `<DefaultItem>` can represent the "no selection" state and renders its caption
 * as the input placeholder rather than a selected value.
 *
 * ## Accessibility
 * Implements the [WAI-ARIA Combobox pattern](https://www.w3.org/WAI/ARIA/apg/patterns/combobox/).
 * Provide `aria-label`, `aria-labelledby`, or `placeholder` + `name` so screen
 * readers can announce the field. Typeahead is enabled: typing jumps to the
 * first matching item in the open listbox.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-combo-box}
 */
export function ComboBox<ItemValue, Value extends ItemValue | ItemValue[]>(
    props: ComboBoxProps<Value>,
) {
    const {
        shown: shownProp,
        setShown: setShownProp,
        helperText,
        name,
        disabled = false,
        placeholder,
        color,
        direction,
        itemsForScroll,
        value: valueProp,
        multiselectable = Array.isArray(valueProp),
        onChange: onChangeProp,
        onToggle,
        dropdown,
        children,
        type,
        allowedPlacements,
        portalProps,
        ...inputProps
    } = props;

    const [shown, setShown] = useControlledState(shownProp, setShownProp, false);

    const itemsProps = useMemo(() => {
        const newProps: ComboboxItemProps<ItemValue>[] = [];
        iterateItems<ItemValue>(children, item => {
            newProps.push(item.props);
        });
        return newProps;
    }, [children]);

    const {
        value,
        onChange,
    } = useComboboxValue<ItemValue, Value>({
        multiselectable,
        value: valueProp,
        onChange: onChangeProp,
        name,
        setShown,
    });

    const defaultItemValue = useMemo(() => {
        let defaultItem: ReactElement<ComboboxItemProps<ItemValue>> | undefined;
        iterateItems<ItemValue>(children, item => {
            if (item.type === DefaultItem) {
                defaultItem = item;
            }
        });
        return defaultItem?.props.value;
    }, [children]);

    const isDefaultItemSelected = value.length === 1 && value[0] === defaultItemValue;

    let selectedIndex = value.length
        ? itemsProps.findIndex(item => item.value === value[0])
        : null;

    if (selectedIndex === -1) {
        selectedIndex = null;
    }

    const {
        getAnchorProps,
        anchorRef,
        getListBoxProps,
    } = useListbox({
        shown,
        setShown,
        direction,
        itemsForScroll,
        typeaheadEnabled: true,
        onToggle,
        type,
        allowedPlacements,
        listNavigationProps: {
            selectedIndex,
        },
    });

    const descriptionId = useId();

    const onKeyDownHandler = () => {
        if (!shown) {
            setShown(true);
            return;
        }

        const {activeIndex} = getListBoxProps();
        if (typeof activeIndex !== 'number') {
            return;
        }
        onChange(itemsProps[activeIndex]);
    };

    const selectedItemsProps = useMemo(() => {
        const itemsPropsByValue = new Map<ItemValue, ComboboxItemProps<ItemValue>>();
        iterateItems<ItemValue>(children, item => {
            itemsPropsByValue.set(item.props.value, item.props);
        });

        return (
            value
                .map(itemValue => itemsPropsByValue.get(itemValue))
                .filter(Boolean) as ComboboxItemProps<ItemValue>[]
        );
    }, [value, children]);

    return (
        <>
            <ComboBoxInput
                aria-label={(placeholder || name) ?? ''}
                aria-describedby={helperText ? descriptionId : undefined}
                {...{
                    disabled,
                    placeholder,
                    color,
                    multiselectable,
                    isDefaultItemSelected,
                    ...getAnchorProps(inputProps),
                    selectedItemsProps,
                    onKeyDownHandler,
                }}
                ref={anchorRef}
            />
            <ComboboxListbox
                aria-label={(placeholder || name) ?? ''}
                {...{
                    isDefaultItemSelected,
                    multiselectable,
                    dropdown,
                    value,
                    portalProps,
                    ...getListBoxProps(),
                    onChange,
                }}>
                {children}
            </ComboboxListbox>
            {helperText ? (
                <FieldDescription id={descriptionId} color={color === 'ERROR' ? 'Types.ERROR' : undefined}>
                    {helperText}
                </FieldDescription>
            ) : null}
        </>
    );
}
